// =============================================================================
// src/features/analytics/analytics-feed-sort-markup.ts — Seletor de ordenacao.
//
// 2026-09-20. Direcao escolhida pelo usuario nos comps: SELETOR AGRUPADO, no
// lugar dos dois botoes de texto ("Mais recentes" / "Mais populares"). Quatro
// criterios x duas direcoes = oito opcoes; oito botoes nao cabem no cabecalho do
// painel nem no drawer mobile, e o CSS de la foi desenhado para dois.
// Referencia de markup no proprio portal: o <select> de granularidade do grafico
// (analytics-summary-markup), o unico do Analytics.
//
// Puro: sem DOM, sem fetch.
// =============================================================================

import { escapeHtml } from '../../app/dom';
import {
  ANALYTICS_FEED_SORTS,
  ANALYTICS_FEED_SORT_FIELD_LABELS,
  ANALYTICS_FEED_SORT_LABELS,
  analyticsFeedSortField,
  analyticsFeedSortIsLiveOnly,
  type AnalyticsFeedSort,
  type AnalyticsFeedSortField,
} from '../../data/analytics-feed-sort';
import type { ProvenanceResolvedSource } from '../../data/data-provenance';

const ANALYTICS_LIVE_ONLY_SORT_TITLE = 'Sentimento chega do monitoramento ao vivo';

function analyticsSortOptionMarkup(
  sort: AnalyticsFeedSort,
  activeSort: AnalyticsFeedSort,
  source: ProvenanceResolvedSource,
): string {
  // Opcao que ordenaria por coluna vazia nasce DESABILITADA em vez de devolver
  // uma ordem inventada — mesmo tratamento da tab de sentimento do grafico.
  const disabled = source !== 'live' && analyticsFeedSortIsLiveOnly(sort);
  const title = disabled ? ` title="${ANALYTICS_LIVE_ONLY_SORT_TITLE}"` : '';
  return `<option value="${sort}"${sort === activeSort ? ' selected' : ''}${disabled ? ' disabled' : ''}${title}>`
    + `${escapeHtml(ANALYTICS_FEED_SORT_LABELS[sort])}</option>`;
}

function analyticsSortGroupMarkup(
  field: AnalyticsFeedSortField,
  activeSort: AnalyticsFeedSort,
  source: ProvenanceResolvedSource,
): string {
  const options = ANALYTICS_FEED_SORTS
    .filter((sort) => analyticsFeedSortField(sort) === field)
    .map((sort) => analyticsSortOptionMarkup(sort, activeSort, source))
    .join('');
  return `<optgroup label="${escapeHtml(ANALYTICS_FEED_SORT_FIELD_LABELS[field])}">${options}</optgroup>`;
}

/**
 * Seletor de ordenacao do feed, agrupado por criterio.
 *
 * @example
 *   buildAnalyticsFeedSortMarkup('data:desc', 'live');
 *   // <div class="u-analytics-feed-sort">…<select data-analytics-feed-sort>…</select></div>
 */
export function buildAnalyticsFeedSortMarkup(
  activeSort: AnalyticsFeedSort,
  source: ProvenanceResolvedSource,
): string {
  const fields: readonly AnalyticsFeedSortField[] = ['data', 'engajamento', 'vistas', 'sentimento'];
  const groups = fields.map((field) => analyticsSortGroupMarkup(field, activeSort, source)).join('');
  return `<div class="u-analytics-feed-sort">
    <label for="analyticsFeedSort">Ordenar</label>
    <select id="analyticsFeedSort" data-analytics-feed-sort>${groups}</select>
  </div>`;
}
